import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "@/data/catalog";
import { useAuth, formatApiError } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4 shrink-0" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.8 6.1C12.4 13.5 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17z" />
    <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.7l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

const AppleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" aria-hidden="true"><path fill="currentColor" d="M16.4 12.6c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.8-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.3 1.2 9.7.8 1.2 1.8 2.5 3 2.4 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.6-1-2.6-3.9zM14.1 5.4c.7-.8 1.1-1.9 1-3.1-1 0-2.1.7-2.8 1.5-.6.7-1.2 1.9-1 3 1 .1 2.1-.6 2.8-1.4z" /></svg>
);

export const SocialLoginButtons = ({ compact = false, testPrefix = "social" }) => {
  const { t } = useTranslation();
  const base = `inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-colors whitespace-nowrap ${compact ? "h-9 px-3 text-[11px]" : "h-11 px-3 text-xs sm:text-sm"}`;
  return (
    <div className={`grid grid-cols-2 gap-2 ${compact ? "" : "w-full"}`} data-testid={`${testPrefix}-buttons`}>
      <button type="button" onClick={startGoogleLogin} data-testid={`${testPrefix}-google`}
        className={`${base} bg-white text-[#0b0b0b] hover:bg-[#f3f3f3] border border-white/80`}>
        <GoogleG /> {t("auth.google")}
      </button>
      <button type="button" onClick={() => toast.info(t("auth.appleSoon"))} data-testid={`${testPrefix}-apple`}
        className={`${base} bg-white text-[#0b0b0b] hover:bg-[#f3f3f3] border border-white/80`}>
        <AppleLogo /> {t("auth.apple")}
      </button>
    </div>
  );
};

// Gestisce il ritorno da Google: {origin}/account#session_id=… → scambia la sessione con il JWT dell'app.
export const AuthCallback = () => {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id") || "";
    axios.post(`${API}/account/google/session`, { session_id: sessionId, language: i18n.language?.slice(0, 2) || "it" })
      .then((res) => {
        login(res.data.token, res.data.user);
        toast.success(t("auth.welcomeBack", { name: res.data.user.name }));
        navigate("/account", { replace: true });
      })
      .catch((er) => {
        toast.error(er.response?.status === 401 ? formatApiError(er.response?.data?.detail, t("auth.err")) : t("auth.googleErr"));
        navigate("/accedi", { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-[#0b0b0b] flex items-center justify-center text-white" data-testid="auth-callback">
      <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
    </div>
  );
};
